import { getBackendUrl } from '@/lib/backendUrl';
import { StoredLogo } from '../components/shared/StoredLogo';

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ClientPortalContext } from '../contexts/ClientPortalContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { LogOut, FileText, Calendar, User, Building2, BarChart3, ClipboardList, Settings, Key, Camera, Mail, Phone, ChevronDown, Loader2 } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { Toaster as SonnerToaster } from 'sonner';
import { getApiError } from '@/lib/utils';

/**
 * Client Portal Layout - Phase 2.2A / 2.6B
 * Shell layout for CLIENT user type. Features will be enabled in later phases:
 * - 2.3A: Reports download
 * - 2.3B: Upcoming audits view
 * - 2.6B: Admin impersonation support
 */
const ClientPortalLayout = ({ children }) => {
  const { user, logout, refreshUser } = useAuth();
  const { isImpersonating, impersonation, isAnonymised, anonymise } = useImpersonation();
  const navigate = useNavigate();
  const location = useLocation();
  const [branding, setBranding] = useState(null);
  // null = loading, true = AP features visible, false = AP features hidden
  const [apEnabled, setApEnabled] = useState(null);
  // 'L1' | 'L2' | 'L3' | 'NONE' | null (loading)
  const [apOverallRole, setApOverallRole] = useState(null);
  
  // User profile state
  const [profileOpen, setProfileOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [profilePicture, setProfilePicture] = useState(null);
  const fileInputRef = useRef(null);

  // Fetch portal branding (logo)
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await api.get('/client/me/portal-branding');
        setBranding(response.data);
        setApEnabled(response.data?.ap_applicability_enabled === true);
        setApOverallRole(response.data?.ap_overall_role ?? 'L3');
      } catch (error) {
        console.error('Failed to fetch portal branding:', error);
      }
    };
    fetchBranding();
  }, []);

  // Initialize profile data when user changes
  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone_number || '',
      });
      setProfilePicture(user.profile_photo_url || null);
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleProfileSave = async () => {
    setProfileLoading(true);
    try {
      await api.put('/users/me/profile', {
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        email: profileData.email,
        phone: profileData.phone,
      });
      if (refreshUser) await refreshUser();
      toast.success('Profile updated successfully');
      setProfileOpen(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(getApiError(error, 'Failed to update profile'));
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setPasswordLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      toast.success('Password changed successfully');
      setPasswordOpen(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      console.error('Failed to change password:', error);
      toast.error(getApiError(error, 'Failed to change password'));
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/users/me/profile-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProfilePicture(response.data.profile_photo_url);
      if (refreshUser) await refreshUser();
      toast.success('Profile picture updated');
    } catch (error) {
      console.error('Failed to upload picture:', error);
      toast.error('Failed to upload profile picture');
    }
  };

  // Resolve profile photo URL (R2 keys need API path)
  const resolvePhotoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('profile_photos/')) {
      return `${getBackendUrl()}/api/users/profile-photo/${url.replace('profile_photos/', '')}`;
    }
    return url;
  };

  // L1 and L2 users land on Analytics; L3 users land on the task worklist.
  const _apIsAnalyticsRole = apOverallRole === 'L1' || apOverallRole === 'L2';

  const navItems = [
    { 
      path: '/client/dashboard', 
      label: 'Dashboard', 
      icon: Building2,
      enabled: true 
    },
    {
      path: _apIsAnalyticsRole ? '/client/action-plans/analytics' : '/client/action-plans',
      label: _apIsAnalyticsRole ? 'Action Plans' : 'My Actions',
      icon: ClipboardList,
      // Only shown when AP is enabled for this customer's scope
      enabled: apEnabled === true
    },
    { 
      path: '/client/reports', 
      label: 'Reports', 
      icon: FileText,
      enabled: true 
    },
    {
      path: '/client/property-dashboard',
      label: 'Property Analytics',
      icon: BarChart3,
      enabled: true
    },
    { 
      path: '/client/audits', 
      label: 'Upcoming Audits', 
      icon: Calendar,
      enabled: true
    },
  ];

  // Determine which user info to display
  // When impersonating, show the impersonated client's info
  // Use environment variable REACT_APP_DISPLAY_NAME to override org name if set
  const displayOrgName = process.env.REACT_APP_DISPLAY_NAME || 
    (user?.organisation_name?.replace(/\s*\(Staging\)\s*/i, '') || 'Abbey 5 Star Ltd');
  
  const displayUser = isImpersonating && impersonation ? {
    first_name: anonymise(impersonation.client_user_name?.split(' ')[0] || 'Client', 'user'),
    last_name: isAnonymised ? '' : (impersonation.client_user_name?.split(' ').slice(1).join(' ') || ''),
    organisation_name: displayOrgName
  } : {
    ...user,
    organisation_name: displayOrgName
  };

  return (
    <ClientPortalContext.Provider value={{ apEnabled, apOverallRole }}>
    <div className={`min-h-screen bg-slate-50 ${isImpersonating ? 'pt-10' : ''}`}>
      <SonnerToaster position="bottom-right" richColors />
      {/* Header - offset by banner height when impersonating */}
      <header className={`bg-white border-b border-slate-200 ${isImpersonating ? 'sticky top-10' : 'sticky top-0'} z-40`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center" style={{ height: '100px' }}>
            {/* Left: Client Logo (Primary) - Clickable to home */}
            <Link to="/client/dashboard" className="flex items-center gap-4 cursor-pointer hover:opacity-90 transition-opacity" data-testid="client-logo-home-link">
              {branding?.logo_url && !isAnonymised ? (
                <div className="flex items-center gap-3">
                  <StoredLogo
                    logoKey={branding.logo_url}
                    alt={branding.display_name || 'Client'}
                    className="w-auto object-contain max-w-[200px]"
                    style={{ height: '84px' }}
                    data-testid="client-portal-logo"
                  />
                  {branding.display_name && (
                    <span className="text-base font-medium text-slate-800 hidden sm:block">
                      {anonymise(branding.display_name, 'org')}
                    </span>
                  )}
                </div>
              ) : (
                /* Fallback when no client logo - show AbbeyZone branding on left */
                <div className="flex items-center gap-3">
                  <img 
                    src="/logo.png" 
                    alt="Abbey 5 Star"
                    className="w-auto object-contain"
                    style={{ height: '60px' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div>
                    <h1 className="text-base font-semibold text-slate-900">
                      AbbeyZone
                    </h1>
                    <p className="text-xs text-slate-500">
                      Performance Insights for Your Properties
                    </p>
                  </div>
                </div>
              )}
            </Link>

            {/* Right: Abbey 5 Star Logo (Secondary) + User Menu */}
            <div className="flex items-center gap-6">
              {/* Abbey 5 Star branding - Secondary (80% of client logo) */}
              {branding?.logo_url && !isAnonymised && (
                <div className="flex items-center gap-2 border-r border-slate-200 pr-6">
                  <img 
                    src="/logo.png" 
                    alt="Abbey 5 Star"
                    className="w-auto object-contain"
                    style={{ height: '67px' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-700">
                      AbbeyZone
                    </p>
                    <p className="text-xs text-slate-400">
                      Performance Insights for Your Properties
                    </p>
                  </div>
                </div>
              )}
              
              {/* User Menu with Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 text-slate-600 hover:text-slate-900" data-testid="user-menu-trigger">
                    {profilePicture ? (
                      <img 
                        src={resolvePhotoUrl(profilePicture)} 
                        alt="Profile" 
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center">
                        <User className="h-4 w-4 text-emerald-600" />
                      </div>
                    )}
                    <span className="text-sm hidden sm:inline">{displayUser?.first_name} {displayUser?.last_name}</span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span>{displayUser?.first_name} {displayUser?.last_name}</span>
                      <span className="text-xs font-normal text-muted-foreground">{isAnonymised ? 'demo@example.com' : user?.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setProfileOpen(true)} data-testid="edit-profile-menu-item">
                    <Settings className="h-4 w-4 mr-2" />
                    Edit Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setPasswordOpen(true)} data-testid="change-password-menu-item">
                    <Key className="h-4 w-4 mr-2" />
                    Change Password
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {!isImpersonating && (
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-menu-item">
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className={`bg-white border-b border-slate-200 ${isImpersonating ? 'sticky top-[140px]' : 'sticky top-[100px]'} z-30`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 -mb-px">
            {navItems.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              if (!item.enabled) {
                return (
                  <div
                    key={item.path}
                    className="flex items-center gap-2 px-4 py-3 text-sm text-slate-400 cursor-not-allowed"
                    title="Coming Soon"
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                    {item.comingSoon && (
                      <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                        Soon
                      </span>
                    )}
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    isActive 
                      ? 'border-emerald-500 text-emerald-600' 
                      : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Footer - Exact duplicate from internal user layout */}
      <footer className="bg-white border-t border-slate-200 py-4 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Abbey 5 Star Ltd" className="h-8 w-auto opacity-70" />
              <span>© {new Date().getFullYear()} Abbey 5 Star Ltd. All rights reserved.</span>
            </div>
            <div className="flex gap-4">
              <Link to="/client/privacy" className="hover:text-foreground">Privacy Policy</Link>
              <Link to="/client/trademark" className="hover:text-foreground">Trademark</Link>
              <Link to="/client/contact" className="hover:text-foreground">Contact</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Edit Profile Dialog */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="sm:max-w-[425px]" data-testid="edit-profile-dialog">
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
            <DialogDescription>Update your account details below.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Profile Picture */}
            <div className="flex items-center gap-4">
              <div className="relative">
                {profilePicture ? (
                  <img src={profilePicture.startsWith('profile_photos/') ? `${getBackendUrl()}/api/users/profile-photo/${profilePicture.replace('profile_photos/', '')}` : profilePicture} alt="Profile" className="h-16 w-16 rounded-full object-cover" />
                ) : (
                  <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center">
                    <User className="h-8 w-8 text-emerald-600" />
                  </div>
                )}
              </div>
              <div>
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="upload-photo-btn">
                  <Camera className="h-4 w-4 mr-2" />
                  Change Photo
                </Button>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePictureUpload} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input id="first_name" data-testid="profile-first-name" value={profileData.first_name} onChange={(e) => setProfileData(p => ({ ...p, first_name: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input id="last_name" data-testid="profile-last-name" value={profileData.last_name} onChange={(e) => setProfileData(p => ({ ...p, last_name: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">
                <Mail className="h-3.5 w-3.5 inline mr-1" />
                Email
              </Label>
              <Input id="email" type="email" data-testid="profile-email" value={profileData.email} onChange={(e) => setProfileData(p => ({ ...p, email: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">
                <Phone className="h-3.5 w-3.5 inline mr-1" />
                Phone
              </Label>
              <Input id="phone" type="tel" data-testid="profile-phone" value={profileData.phone} onChange={(e) => setProfileData(p => ({ ...p, phone: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileOpen(false)}>Cancel</Button>
            <Button onClick={handleProfileSave} disabled={profileLoading} data-testid="save-profile-btn">
              {profileLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordOpen} onOpenChange={(open) => { setPasswordOpen(open); if (!open) setPasswordData({ current_password: '', new_password: '', confirm_password: '' }); }}>
        <DialogContent className="sm:max-w-[400px]" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Enter your current password and choose a new one.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Current Password</Label>
              <Input id="current_password" type="password" data-testid="current-password" value={passwordData.current_password} onChange={(e) => setPasswordData(p => ({ ...p, current_password: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <Input id="new_password" type="password" data-testid="new-password" value={passwordData.new_password} onChange={(e) => setPasswordData(p => ({ ...p, new_password: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input id="confirm_password" type="password" data-testid="confirm-password" value={passwordData.confirm_password} onChange={(e) => setPasswordData(p => ({ ...p, confirm_password: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordOpen(false)}>Cancel</Button>
            <Button onClick={handlePasswordChange} disabled={passwordLoading} data-testid="save-password-btn">
              {passwordLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </ClientPortalContext.Provider>
  );
};

export default ClientPortalLayout;
